import type { TitleConfig } from "@/types/shared/title";
import { getFontStack } from "./fontUtils";

export type SectionTitleScale = NonNullable<TitleConfig["fontSizeScale"]>;
export type SectionTitleVariant = "title" | "subtitle";

export type CardTitleSize = "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";
export type CardDescriptionSize = "xs" | "sm" | "md" | "lg";
export type BadgeTextSize = "sm" | "md" | "lg";
export type ButtonTextSize = "sm" | "md" | "lg" | "xl";

type TokenRange = { min: number; max: number };

const clampFromRange = (range: TokenRange, scale: number): string => {
  const round = (value: number) => Math.round(value * 10000) / 10000;
  const scaledMin = round(range.min * scale);
  const scaledMax = round(range.max * scale);
  const delta = round(scaledMax - scaledMin);
  const slope = round(delta * 1.1);

  return `clamp(${scaledMin}rem, calc(${scaledMin}rem + ${slope}vw), ${scaledMax}rem)`;
};

const TITLE_SCALE_STEPS: Record<SectionTitleScale, TokenRange> = {
  xs: { min: 1, max: 1 },
  sm: { min: 1, max: 1.25 },
  md: { min: 1.125, max: 1.5 },
  lg: { min: 1.25, max: 1.875 },
  xl: { min: 1.5, max: 2.25 },
  "2xl": { min: 1.5, max: 3 },
  "3xl": { min: 1.875, max: 3.75 },
  "4xl": { min: 2.25, max: 4.5 },
  "5xl": { min: 3, max: 6 },
};

const SUBTITLE_SCALE_STEPS: Record<SectionTitleScale, TokenRange> = {
  xs: { min: 1, max: 1 },
  sm: { min: 1, max: 1.25 },
  md: { min: 1.125, max: 1.5 },
  lg: { min: 1.25, max: 1.875 },
  xl: { min: 1.5, max: 2.25 },
  "2xl": { min: 1.5, max: 3 },
  "3xl": { min: 1.875, max: 3.75 },
  "4xl": { min: 2.25, max: 4.5 },
  "5xl": { min: 3, max: 6 },
};

const CARD_TITLE_SCALE_STEPS: Record<CardTitleSize, TokenRange> = {
  xs: { min: 0.875, max: 1 },
  sm: { min: 1, max: 1 },
  md: { min: 1, max: 1.125 },
  lg: { min: 1.125, max: 1.25 },
  xl: { min: 1.25, max: 1.5 },
  "2xl": { min: 1.5, max: 1.875 },
  "3xl": { min: 1.5, max: 2.25 },
};

const CARD_DESCRIPTION_SCALE_STEPS: Record<CardDescriptionSize, TokenRange> = {
  xs: { min: 0.875, max: 1 },
  sm: { min: 1, max: 1 },
  md: { min: 1, max: 1.125 },
  lg: { min: 1, max: 1.125 },
};

const TEXT_ROLE_STEPS: Record<string, TokenRange> = {
  body: { min: 1, max: 1.125 },
  "body-sm": { min: 0.875, max: 1 },
  "body-lg": { min: 1.125, max: 1.25 },
  lead: { min: 1.25, max: 1.875 },
  "lead-lg": { min: 1.5, max: 2.25 },
  caption: { min: 0.75, max: 0.875 },
  "caption-lg": { min: 1, max: 1.125 },
  "stat-value": { min: 1.5, max: 2.25 },
  "stat-value-lg": { min: 2.25, max: 3.75 },
  "stat-label": { min: 0.875, max: 1 },
  "stat-label-lg": { min: 1, max: 1.125 },
  display: { min: 1.875, max: 6 },
  "index-number": { min: 1.125, max: 1.5 },
};

const BADGE_SCALE_STEPS: Record<BadgeTextSize, TokenRange> = {
  sm: { min: 0.875, max: 1 },
  md: { min: 1, max: 1.125 },
  lg: { min: 1.125, max: 1.25 },
};

const BUTTON_SCALE_STEPS: Record<ButtonTextSize, TokenRange> = {
  sm: { min: 0.875, max: 1 },
  md: { min: 1, max: 1.125 },
  lg: { min: 1, max: 1.125 },
  xl: { min: 1.125, max: 1.25 },
};

const mapScaleVars = <T extends string>(
  prefix: string,
  steps: Record<T, TokenRange>,
  scale: number
): Record<string, string> => {
  return Object.entries(steps).reduce<Record<string, string>>(
    (acc, [key, range]) => {
      acc[`--lp-${prefix}-${key}`] = clampFromRange(range as TokenRange, scale);
      return acc;
    },
    {}
  );
};

export const SECTION_TITLE_SCALE_CLASSES: Record<
  SectionTitleVariant,
  Record<SectionTitleScale, string>
> = {
  title: {
    xs: "text-[length:var(--lp-title-xs)]",
    sm: "text-[length:var(--lp-title-sm)]",
    md: "text-[length:var(--lp-title-md)]",
    lg: "text-[length:var(--lp-title-lg)]",
    xl: "text-[length:var(--lp-title-xl)]",
    "2xl": "text-[length:var(--lp-title-2xl)]",
    "3xl": "text-[length:var(--lp-title-3xl)]",
    "4xl": "text-[length:var(--lp-title-4xl)]",
    "5xl": "text-[length:var(--lp-title-5xl)]",
  },
  subtitle: {
    xs: "text-[length:var(--lp-subtitle-xs)]",
    sm: "text-[length:var(--lp-subtitle-sm)]",
    md: "text-[length:var(--lp-subtitle-md)]",
    lg: "text-[length:var(--lp-subtitle-lg)]",
    xl: "text-[length:var(--lp-subtitle-xl)]",
    "2xl": "text-[length:var(--lp-subtitle-2xl)]",
    "3xl": "text-[length:var(--lp-subtitle-3xl)]",
    "4xl": "text-[length:var(--lp-subtitle-4xl)]",
    "5xl": "text-[length:var(--lp-subtitle-5xl)]",
  },
};

export const CARD_TITLE_SIZE_CLASSES: Record<CardTitleSize, string> = {
  xs: "text-[length:var(--lp-card-title-xs)]",
  sm: "text-[length:var(--lp-card-title-sm)]",
  md: "text-[length:var(--lp-card-title-md)]",
  lg: "text-[length:var(--lp-card-title-lg)]",
  xl: "text-[length:var(--lp-card-title-xl)]",
  "2xl": "text-[length:var(--lp-card-title-2xl)]",
  "3xl": "text-[length:var(--lp-card-title-3xl)]",
};

export const CARD_TITLE_SIZE_SM_CLASSES: Record<CardTitleSize, string> = {
  xs: "sm:text-[length:var(--lp-card-title-xs)]",
  sm: "sm:text-[length:var(--lp-card-title-sm)]",
  md: "sm:text-[length:var(--lp-card-title-md)]",
  lg: "sm:text-[length:var(--lp-card-title-lg)]",
  xl: "sm:text-[length:var(--lp-card-title-xl)]",
  "2xl": "sm:text-[length:var(--lp-card-title-2xl)]",
  "3xl": "sm:text-[length:var(--lp-card-title-3xl)]",
};

export const CARD_DESCRIPTION_SIZE_CLASSES: Record<CardDescriptionSize, string> = {
  xs: "text-[length:var(--lp-card-desc-xs)]",
  sm: "text-[length:var(--lp-card-desc-sm)]",
  md: "text-[length:var(--lp-card-desc-md)]",
  lg: "text-[length:var(--lp-card-desc-lg)]",
};

export const CARD_DESCRIPTION_SIZE_SM_CLASSES: Record<
  CardDescriptionSize,
  string
> = {
  xs: "sm:text-[length:var(--lp-card-desc-xs)]",
  sm: "sm:text-[length:var(--lp-card-desc-sm)]",
  md: "sm:text-[length:var(--lp-card-desc-md)]",
  lg: "sm:text-[length:var(--lp-card-desc-lg)]",
};

export const BADGE_TEXT_SIZE_CLASSES: Record<BadgeTextSize, string> = {
  sm: "text-[length:var(--lp-text-badge-sm)]",
  md: "text-[length:var(--lp-text-badge-md)]",
  lg: "text-[length:var(--lp-text-badge-lg)]",
};

export const BUTTON_TEXT_SIZE_CLASSES: Record<ButtonTextSize, string> = {
  sm: "text-[length:var(--lp-text-button-sm)]",
  md: "text-[length:var(--lp-text-button-md)]",
  lg: "text-[length:var(--lp-text-button-lg)]",
  xl: "text-[length:var(--lp-text-button-xl)]",
};

export const TYPOGRAPHY_ROLE_CLASSES = {
  body: "text-[length:var(--lp-text-body)]",
  bodySm: "text-[length:var(--lp-text-body-sm)]",
  bodyLg: "text-[length:var(--lp-text-body-lg)]",
  lead: "text-[length:var(--lp-text-lead)]",
  leadLg: "text-[length:var(--lp-text-lead-lg)]",
  caption: "text-[length:var(--lp-text-caption)]",
  captionLg: "text-[length:var(--lp-text-caption-lg)]",
  statValue: "text-[length:var(--lp-text-stat-value)]",
  statValueLg: "text-[length:var(--lp-text-stat-value-lg)]",
  statLabel: "text-[length:var(--lp-text-stat-label)]",
  statLabelLg: "text-[length:var(--lp-text-stat-label-lg)]",
  indexNumber: "text-[length:var(--lp-text-index-number)]",
  button: "text-[length:var(--lp-text-button-sm)] font-medium",
};

export const DISPLAY_TEXT_CLASS = "text-[length:var(--lp-text-display)]";

export const getSectionTitleSizeClass = (
  scale: SectionTitleScale,
  variant: SectionTitleVariant
): string => {
  return SECTION_TITLE_SCALE_CLASSES[variant][scale];
};

/**
 * Build font-family CSS variables for centralized font enforcement.
 * These variables provide a fallback mechanism for elements without inline styles.
 */
export const buildFontFamilyCssVars = (
  bodyFont?: string,
  headingFont?: string
): Record<string, string> => {
  return {
    '--lp-font-body': bodyFont ? getFontStack(bodyFont) : 'inherit',
    '--lp-font-heading': headingFont ? getFontStack(headingFont) : 'var(--lp-font-body)',
  };
};

export const buildTypographyCssVars = (
  scale = 1,
  bodyFont?: string,
  headingFont?: string
): Record<string, string> => {
  return {
    ...mapScaleVars("text", TEXT_ROLE_STEPS, scale),
    ...mapScaleVars("title", TITLE_SCALE_STEPS, scale),
    ...mapScaleVars("subtitle", SUBTITLE_SCALE_STEPS, scale),
    ...mapScaleVars("card-title", CARD_TITLE_SCALE_STEPS, scale),
    ...mapScaleVars("card-desc", CARD_DESCRIPTION_SCALE_STEPS, scale),
    ...mapScaleVars("text-badge", BADGE_SCALE_STEPS, scale),
    ...mapScaleVars("text-button", BUTTON_SCALE_STEPS, scale),
    ...buildFontFamilyCssVars(bodyFont, headingFont),
  };
};
